import { compareDesc, isAfter, parseISO, subDays } from 'date-fns';
import * as React from 'react';
import { css, html } from 'react-strict-dom';
import { useForm } from 'react-typed-form';

import { Api, useApiFormSubmit } from '../../api';
import { Spacer, Text } from '../../components';
import { Color } from '../../constants';
import {
  FieldText,
  FieldTextRequired,
  FormWrap,
  SubmitButton,
} from '../../form';
import { useMyUserClient } from '../../hooks';
import { Model } from '../../models';
import BuildDetail from './BuildDetail';

type FormValues = Omit<Api.Build.W, 'buildNumber'> & { buildNumber: string };

type Props = {
  release: Api.Release.V;
  invalidate: () => unknown;
};

export default function SectionBuilds({ release, invalidate }: Props) {
  const myUser = useMyUserClient();

  const [adding, setAdding] = React.useState(false);

  const formSubmit = useApiFormSubmit('POST', '/builds', { invalidate });
  const form = useForm<Partial<FormValues>>({
    defaultValues: {
      release: `/releases/${release.id}`,
      buildNumber: '',
      environment: '',
      link: '',
    },
    onSubmit: (values, helpers) =>
      formSubmit(
        { ...values, buildNumber: parseInt(values.buildNumber ?? '', 10) },
        helpers
      ),
  });

  const visibleBuilds = myUser.agencyRole
    ? [...release.builds]
    : release.builds.filter((b) => b.qaApprovedAt);
  visibleBuilds.sort((a, b) =>
    compareDesc(parseISO(a.createdAt), parseISO(b.createdAt))
  );

  return (
    <html.div>
      <Text as="h3" style={styles.sectionHeading}>
        Builds
        {myUser.agencyRole && (
          <html.a
            style={styles.promptButton}
            onClick={() => setAdding(!adding)}
          >
            <i className="fa-solid fa-plus" />
          </html.a>
        )}
      </Text>

      {adding && (
        <FormWrap
          formErrorList={form.formErrorList}
          handleSubmit={form.handleSubmit}
        >
          <FieldTextRequired field={form.getField('buildNumber')} />
          <FieldText field={form.getField('platform')} />
          <FieldTextRequired field={form.getField('environment')} />
          <FieldTextRequired field={form.getField('link')} />
          <SubmitButton {...form} label="Submit" />
          <Spacer />
        </FormWrap>
      )}

      {visibleBuilds.length === 0 && myUser.agencyRole && (
        <Text as="p" style={styles.noBuildsText}>
          No builds added yet
        </Text>
      )}

      {visibleBuilds.length === 0 &&
        !myUser.agencyRole &&
        isAfter(parseISO(release.createdAt), subDays(new Date(), 30)) && (
          <html.div style={styles.noBuilds}>
            <i
              className="fa-solid fa-gears"
              style={{
                color: Color.textDisabled,
                fontSize: 24,
                display: 'block',
                marginBottom: 8,
              }}
            />
            <Text>
              No builds are available for you yet - this release is still under
              QA testing and development.
            </Text>
          </html.div>
        )}

      {visibleBuilds.map((build, i) => (
        <React.Fragment key={build.id}>
          {i > 0 && <Spacer size={0.5} />}
          <BuildDetail
            key={build.id}
            build={build}
            status={Model.Build.getStatus(build, release.builds)}
            invalidate={invalidate}
          />
        </React.Fragment>
      ))}
    </html.div>
  );
}

const styles = css.create({
  sectionHeading: {
    marginBlockStart: 25,
    marginBlockEnd: 15,
    color: '#1C1D20',
    fontSize: 18,
    fontWeight: '600',
  },
  promptButton: {
    color: '#ccd',
    cursor: 'pointer',
    fontSize: 16,
    transition: 'color 0.2s',
    marginLeft: 10,
    ':hover': {
      color: '#0366d6',
    },
  },
  noBuildsText: {
    color: '#7f858b',
  },
  noBuilds: {
    backgroundColor: '#fafafa',
    borderWidth: 1,
    borderStyle: 'solid',
    borderColor: '#ddd',
    borderRadius: 8,
    paddingBlock: 10,
    paddingInline: 15,
  },
});
